/** Private console stars. Home is always first under Starred and cannot be removed. */

const STORAGE_PREFIX = 'nextcompli.console.starredSections.';

export const PERMANENT_STARRED_SECTION_IDS = ['home'] as const;

export function isPermanentStarredSection(id: string): boolean {
  return (PERMANENT_STARRED_SECTION_IDS as readonly string[]).includes(id.trim());
}

/** Home leads the list. A saved copy of Home is not a second star. */
export function withPermanentStars(ids: readonly string[]): string[] {
  const rest = ids.filter((id) => !isPermanentStarredSection(id));
  return [...PERMANENT_STARRED_SECTION_IDS, ...rest];
}

export function consoleStarStorageKey(userKey: string): string {
  const scope = userKey.trim().toLowerCase() || 'anonymous';
  return `${STORAGE_PREFIX}${scope}`;
}

export function parseStarredSectionIds(raw: string | null): string[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    const seen = new Set<string>();
    const next: string[] = [];
    parsed.forEach((value) => {
      const id = String(value || '').trim();
      if (!id || seen.has(id)) {
        return;
      }
      seen.add(id);
      next.push(id);
    });
    return next;
  } catch {
    return [];
  }
}

/** A new star is appended. Removing one leaves the rest in the order the person chose. */
export function toggleStarredSectionId(ids: readonly string[], id: string): string[] {
  const target = id.trim();
  if (!target || isPermanentStarredSection(target)) {
    return [...ids];
  }
  if (ids.includes(target)) {
    return ids.filter((item) => item !== target);
  }
  return [...ids, target];
}

export function starredSectionItems<T extends { id: string }>(
  items: readonly T[],
  ids: readonly string[],
): T[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  return withPermanentStars(ids)
    .map((id) => byId.get(id))
    .filter((item): item is T => Boolean(item));
}
